import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, UserPlus, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, email: "", name: "" });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`)
      .then((r) => {
        setState({ loading: false, valid: r.data.valid, email: r.data.email, name: r.data.name || "",
                   used: r.data.used, expired: r.data.expired });
        setName(r.data.name || "");
      })
      .catch(() => setState({ loading: false, valid: false }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/invite/accept", { token, name, password });
      toast.success("Account created", { description: "Sign in and set up your authenticator 2FA." });
      navigate("/login");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020817] px-6 py-12">
      <div className="w-full max-w-sm fade-up">
        <div className="flex items-center gap-3 mb-10">
          <Logo size={44} />
          <div>
            <div className="font-head text-2xl font-extrabold tracking-tight text-white">SoverenX</div>
            <div className="overline text-slate-500">Systems · Pentest Platform</div>
          </div>
        </div>

        {state.loading ? (
          <div className="text-slate-400 animate-pulse">Validating invite…</div>
        ) : !state.valid ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-5">
            <div className="flex items-center gap-2 text-red-400 mb-2"><Warning size={20} weight="fill" /><span className="font-semibold">Invite unavailable</span></div>
            <p className="text-sm text-slate-300">
              {state.used ? "This invite has already been used." : state.expired ? "This invite has expired." : "This invite link is invalid."}
              {" "}Please request a new invite from your SoverenX administrator.
            </p>
            <Button data-testid="invite-goto-login" onClick={() => navigate("/login")} className="mt-4 bg-blue-600 hover:bg-blue-500 text-white">
              Go to sign in
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-blue-400 mb-2"><UserPlus size={20} weight="bold" /><span className="overline">You've been invited</span></div>
            <h1 className="font-head text-3xl font-bold tracking-tight text-white">Create your account</h1>
            <p className="text-sm text-slate-400 mt-2 mb-6">
              For <span className="text-blue-300 font-mono">{state.email}</span>. You'll join as a <b>Pentester</b> and set up authenticator 2FA on first sign-in.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-slate-300">Full name</Label>
                <Input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 bg-[#0f172a] border-[#1e293b] text-white" required />
              </div>
              <div>
                <Label className="text-slate-300">Password</Label>
                <Input data-testid="invite-password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
                  className="mt-1.5 bg-[#0f172a] border-[#1e293b] text-white" required />
              </div>
              <div>
                <Label className="text-slate-300">Confirm password</Label>
                <Input data-testid="invite-confirm-password-input" type="password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password"
                  className="mt-1.5 bg-[#0f172a] border-[#1e293b] text-white" required />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-amber-400">Passwords do not match yet.</p>
              )}
              {error && <div data-testid="invite-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{error}</div>}
              <Button data-testid="invite-submit-button" type="submit" disabled={submitting || !name || password.length < 8 || password !== confirmPassword}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md h-11">
                {submitting ? "Creating…" : "Create account"}
              </Button>
            </form>
          </>
        )}

        <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck size={15} weight="fill" className="text-blue-500" />
          Authenticator 2FA is required for every account · data in Australia
        </div>
      </div>
    </div>
  );
}
